/**
 * ActionEntryDialog - shared dialog for displaying workflow action log entries.
 *
 * Used by both admin and consumer claim detail pages to show
 * notes, contact messages, captured values, and sent emails from workflow actions.
 */

import { useState } from 'react'
import { Mail, StickyNote, FileText, ChevronDown, ChevronUp, Send, Reply, UserPlus, Pencil, Paperclip } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Separator } from '@/shared/components/ui/separator'

// ── Types ─────────────────────────────────────────────────

export interface EmailSent {
  uuid: string
  recipient_email: string
  recipient_name: string
  recipient_kind: string
  subject: string
  template_slug: string
  status: string
  rendered_html: string
  created_at: string | null
}

interface ActionLogEntry {
  uuid: string
  action_key: string
  action_label: string
  action_icon: string | null
  to_state_label: string | null
  initiated_by: {
    type: 'USER' | 'SYSTEM'
    user: { uuid: string; email: string; name?: string } | null
  }
  parameters: Record<string, unknown> | null
  captured_values: Record<string, unknown> | null
  emails_sent?: EmailSent[]
  created_at: string
}

interface ActionAttachment {
  uuid: string
  file_url: string | null
  file_name: string
  file_type?: string
  file_size?: number
  mime_type: string
}

interface ActionEntryDialogProps {
  entry: ActionLogEntry | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Map of sender role keys to display labels (e.g. { consumer: 'You', tenant: 'Admin' }) */
  roleLabels: Record<string, string>
  /**
   * WPS-138 #11: when true, suppress the internal role label for staff senders
   * (tenant/staff/dealer/system) so an internal user is shown by first name
   * only on a customer-facing surface. "You" (the consumer's own entries) still
   * shows. Defaults to false, so admin callers are unchanged.
   */
  hideRoleLabel?: boolean
  /** Format a captured value for display. Falls back to String() if not provided. */
  formatValue?: (value: unknown, key: string) => string
  /** Format an ISO timestamp for display */
  formatTimestamp: (iso: string) => string
  /**
   * When provided, a "Reply" button is shown for customer-message entries.
   * The caller wires this to open its contact/message composer. Reply is only
   * offered for contact messages (not internal notes or state-change entries).
   */
  onReply?: () => void
}

// ── Helpers ───────────────────────────────────────────────

function defaultFormatValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    return value.length ? value.map((item) => String(item)).join(', ') : '-'
  }
  return String(value)
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const RECIPIENT_KIND_LABELS: Record<string, string> = {
  consumer: 'Customer',
  contractor: 'Dealer',
  third_party: 'Third Party',
}

// ── Email Preview ────────────────────────────────────────

function EmailPreviewCard({
  email,
  formatTimestamp,
  defaultExpanded = false,
}: {
  email: EmailSent
  formatTimestamp: (iso: string) => string
  defaultExpanded?: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const kindLabel = RECIPIENT_KIND_LABELS[email.recipient_kind] || email.recipient_kind
  const isFailed = email.status === 'failed' || email.status === 'bounced'

  return (
    <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex h-auto w-full items-start justify-between gap-3 rounded-none px-3 py-2.5 text-left"
      >
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Send className="h-3 w-3 shrink-0 text-[var(--blue)]" />
            <span className="truncate text-[12px] font-medium text-[var(--ink)]">
              {email.subject || 'Email sent'}
            </span>
            {isFailed && (
              <Badge variant="destructive" className="text-[9px]">
                {email.status}
              </Badge>
            )}
          </div>
          <span className="text-[11px] text-[var(--ink-3)]">
            To: {email.recipient_name ? `${email.recipient_name} (${email.recipient_email})` : email.recipient_email}
            {kindLabel ? ` · ${kindLabel}` : ''}
          </span>
          {email.created_at && (
            <span className="text-[10px] text-[var(--ink-3)]">
              {formatTimestamp(email.created_at)}
            </span>
          )}
        </div>
        {email.rendered_html && (
          <span className="mt-1 shrink-0">
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5 text-[var(--ink-3)]" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-[var(--ink-3)]" />
            )}
          </span>
        )}
      </Button>
      {expanded && email.rendered_html && (
        <div className="border-t-[0.5px] border-t-[var(--rule)]">
          <iframe
            srcDoc={email.rendered_html}
            sandbox=""
            title={`Email preview: ${email.subject}`}
            className="h-[520px] w-full rounded-b-lg bg-white"
          />
        </div>
      )}
    </div>
  )
}

// ── Component ─────────────────────────────────────────────

export function ActionEntryDialog({
  entry,
  open,
  onOpenChange,
  roleLabels,
  hideRoleLabel = false,
  formatValue,
  formatTimestamp,
  onReply,
}: ActionEntryDialogProps) {
  const { t } = useTranslation()
  if (!entry) return null

  const sender =
    entry.initiated_by.type === 'SYSTEM'
      ? 'System'
      : entry.initiated_by.user?.name || entry.initiated_by.user?.email || 'Unknown'
  const params = entry.parameters as Record<string, unknown> | null
  const message = params?.message as string | undefined
  const attachments = Array.isArray(params?.attachments)
    ? (params.attachments as ActionAttachment[])
    : []
  const senderRole = params?.sender_role as string | undefined
  const isContact = entry.action_key === 'consumer_contact'
  const isNote = entry.action_key === 'note' || isContact
  // Duplicate-review events are logged with action_key only (no to_state),
  // so map them to a friendly title here, matching the timeline label.
  const duplicateTitle =
    entry.action_key === 'resolve_duplicates'
      ? 'Duplicates reviewed'
      : entry.action_key === 'reopen_duplicates'
        ? 'Duplicate review re-opened'
        : null
  // Assignment entries are logged with action_key="assign" against the current
  // (unchanged) state, so give them a friendly title rather than the state name.
  const isAssign = entry.action_key === 'assign'
  const assignNewAssignee = (
    entry.captured_values as Record<string, unknown> | null
  )?.new_assignee as string | null | undefined
  const assignTitle = isAssign
    ? assignNewAssignee
      ? t('claims.activityAssignedTo', 'Assigned to {{newAssignee}}', {
          newAssignee: assignNewAssignee,
        })
      : t('claims.activityUnassigned', 'Unassigned')
    : null
  // Attribute edits are logged with action_key="edit_details" (no to_state),
  // so give them a friendly title matching the timeline label.
  const isEdit = entry.action_key === 'edit_details'
  const editTitle = isEdit
    ? t('claims.activityDetailsEdited', 'Details edited')
    : null
  // WPS-157 §18: transfer non-transition events (payment received, proof
  // verified, fee waived, ownership handover) - friendly titles matching the
  // timeline labels. Keys are transfer-specific.
  const transferEventTitles: Record<string, [string, string]> = {
    record_payment_received: [
      'transfers.activityPaymentReceived',
      'Payment received',
    ],
    verify_proof_of_ownership: [
      'transfers.activityProofVerified',
      'Proof of ownership verified',
    ],
    waive_fee: ['transfers.activityFeeWaived', 'Fee waived'],
    owner_updated_via_transfer: [
      'transfers.activityOwnerUpdated',
      'Ownership updated to new owner',
    ],
  }
  const transferEventTitle = transferEventTitles[entry.action_key]
    ? t(
        transferEventTitles[entry.action_key][0],
        transferEventTitles[entry.action_key][1],
      )
    : null
  // Evidence add/remove events (action_key only, no to_state) - friendly titles
  // matching the timeline labels.
  const isEvidenceAdd = entry.action_key === 'evidence_added'
  const isEvidenceRemove = entry.action_key === 'evidence_removed'
  const evidenceTitle = isEvidenceAdd
    ? t('claims.activityEvidenceAdded', 'Evidence added')
    : isEvidenceRemove
      ? t('claims.activityEvidenceRemoved', 'Evidence removed')
      : null
  // WPS-138 #11: drop the internal role label on customer-facing surfaces
  // (everything except the consumer's own "You"), so a staff sender shows by
  // first name only. Falsy result renders no badge.
  const roleLabel =
    senderRole && !(hideRoleLabel && senderRole !== 'consumer')
      ? roleLabels[senderRole] || senderRole
      : undefined
  const capturedVals = entry.captured_values as Record<string, unknown> | null
  const fmt = formatValue ?? defaultFormatValue
  // WPS-157 §1/§16: a field-edit event captures the before/after of each field
  // as {old_values, new_values} (see update_transfer_details). Render it as a
  // real old -> new diff, like the entity-history card, instead of two opaque
  // "[object Object]" rows. Any OTHER captured keys still render generically.
  const oldVals = isPlainRecord(capturedVals?.old_values) ? capturedVals!.old_values : null
  const newVals = isPlainRecord(capturedVals?.new_values) ? capturedVals!.new_values : null
  const hasDiff = Boolean(oldVals && newVals)
  const diffKeys = hasDiff
    ? Array.from(new Set([...Object.keys(oldVals!), ...Object.keys(newVals!)])).filter(
        (k) => fmt(oldVals![k], k) !== fmt(newVals![k], k),
      )
    : []
  const otherCapturedEntries = capturedVals
    ? Object.entries(capturedVals).filter(
        ([k]) => !(hasDiff && (k === 'old_values' || k === 'new_values')),
      )
    : []
  const hasCaptured = diffKeys.length > 0 || otherCapturedEntries.length > 0
  const emailsSent = entry.emails_sent ?? []
  const hasEmails = emailsSent.length > 0
  const hasContent = Boolean(message) || hasCaptured || attachments.length > 0 || hasEmails
  const needsWideDialog = hasEmails && emailsSent.some((e) => e.rendered_html)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={needsWideDialog ? 'sm:max-w-4xl' : 'sm:max-w-md'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isContact ? (
              <Mail className="h-4 w-4 text-[var(--blue)]" />
            ) : isNote ? (
              <StickyNote className="h-4 w-4 text-[var(--amber,#d97706)]" />
            ) : isAssign ? (
              <UserPlus className="h-4 w-4 text-[var(--blue)]" />
            ) : isEdit ? (
              <Pencil className="h-4 w-4 text-[var(--green)]" />
            ) : isEvidenceAdd || isEvidenceRemove ? (
              <Paperclip
                className={
                  isEvidenceAdd
                    ? 'h-4 w-4 text-[var(--green)]'
                    : 'h-4 w-4 text-[var(--ink-3)]'
                }
              />
            ) : hasEmails ? (
              <Send className="h-4 w-4 text-[var(--blue)]" />
            ) : (
              <FileText className="h-4 w-4 text-[var(--green)]" />
            )}
            {duplicateTitle ||
              assignTitle ||
              editTitle ||
              evidenceTitle ||
              transferEventTitle ||
              entry.to_state_label ||
              entry.action_label}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--ink-3)]">
            {sender === 'System' ? 'System' : `From ${sender}`}
            {roleLabel ? ` (${roleLabel})` : ''}
            {' · '}
            {formatTimestamp(entry.created_at)}
          </DialogDescription>
        </DialogHeader>
        {message && (
          <div className="whitespace-pre-wrap text-base leading-relaxed text-[var(--ink)]">
            {message}
          </div>
        )}
        {attachments.length > 0 && (
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)]">
            {attachments.map((attachment) => (
              <Button
                key={attachment.uuid}
                type="button"
                variant="ghost"
                onClick={() => attachment.file_url && window.open(attachment.file_url, '_blank')}
                className="flex h-auto w-full items-center justify-between gap-3 rounded-none border-b-[0.5px] border-b-[var(--rule-light)] px-3 py-2.5 text-left last:border-b-0 hover:bg-[var(--bg-subtle)]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                  <span className="truncate text-[12px] text-[var(--ink)]">
                    {attachment.file_name || attachment.uuid.slice(0, 8)}
                  </span>
                </span>
                <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[var(--green)]">
                  Open
                </span>
              </Button>
            ))}
          </div>
        )}
        {diffKeys.length > 0 && (
          <div className="space-y-2">
            {diffKeys.map((key) => (
              <div key={key} className="flex flex-col gap-0.5">
                <span className="text-sm font-medium uppercase tracking-[0.06em] text-[var(--ink-3)]">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-base text-[var(--ink)]">
                  <span className="text-[var(--ink-3)] line-through">
                    {fmt(oldVals![key], key)}
                  </span>
                  <span className="mx-1.5 text-[var(--ink-3)]">→</span>
                  <span>{fmt(newVals![key], key)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
        {otherCapturedEntries.length > 0 && (
          <div className="space-y-2">
            {otherCapturedEntries.map(([key, value]) => (
              <div key={key} className="flex flex-col gap-0.5">
                <span className="text-sm font-medium uppercase tracking-[0.06em] text-[var(--ink-3)]">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-base text-[var(--ink)]">
                  {fmt(value, key)}
                </span>
              </div>
            ))}
          </div>
        )}
        {hasEmails && (
          <>
            {(Boolean(message) || hasCaptured || attachments.length > 0) && <Separator />}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  Emails sent ({emailsSent.length})
                </span>
              </div>
              {emailsSent.map((email, idx) => (
                <EmailPreviewCard
                  key={email.uuid}
                  email={email}
                  formatTimestamp={formatTimestamp}
                  defaultExpanded={emailsSent.length === 1 && idx === 0}
                />
              ))}
            </div>
          </>
        )}
        {!hasContent && (
          <div className="text-base text-[var(--ink-3)]">No additional details</div>
        )}
        {onReply && isContact && (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onReply}
              className="cursor-pointer"
            >
              <Reply className="h-3.5 w-3.5" />
              Reply
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
